import type { Reducer } from 'redux';
import { reducer as formReducer } from 'redux-form';
import { userReducer, USER_NAMESPACE } from '@core/auth/redux';
import { reducer as driftReducer } from '@core/drift';
import { extLinkEnvReducer } from '@core/external-links/redux';
import { fetchReducer } from '@core/redux-fetch/redux';
import { reducer as plansReducer } from '@core/subscription-plans';
import { challengesReducer as challenges } from '@engage/challenges/redux';
import { CLASS_DETAIL_NAMESPACE, classDetailReducer } from '@engage/class-detail/redux';
import { libraryReducer } from '@engage/library/redux';
import { memberReducer } from '@engage/members/redux';
import { MEMBERS_NAMESPACE } from '@engage/members/selectors';
import {
  friendCountsReducer,
  friendsReducer,
  overviewReducer,
  OVERVIEW_NAMESPACE,
} from '@engage/overview/redux';
import {
  SCHEDULE_NAMESPACE,
  scheduleReducer,
} from '@engage/scheduled-classes/redux/scheduledClasses';
import { SETTINGS_NAMESPACE, settingsReducer } from '@engage/settings/redux';
import { subscriptionReducer } from '@engage/subscriptions/redux/subscriptions';
import {
  BUFFER_METRICS_NAMESPACE,
  bufferMetricsReducer,
} from '@engage/video/bufferMetrics';
import { VIDEO_REDUCER_NAMESPACE } from '@engage/video/redux';
import { workoutHistoryReducer } from '@engage/workout-history/redux';
import { workoutsReducer } from '@engage/workouts/redux';
import { classesReducer } from '@members/classes/redux';
import { CLASSES_NAMESPACE } from '@members/classes/selectors';
import { isProdEnv } from '@members/env';
import { facebookModalReducer } from '@members/facebook/redux/facebookModal';
import { mergeEnabledFeaturesWithEnv } from '@members/feature-toggles/mergeEnabledFeaturesWithEnv';
import { mergeVariablesWithEnv } from '@members/feature-toggles/mergeVariablesWithEnv';
import { FEATURE_TOGGLE_NAMESPACE } from '@members/feature-toggles/redux';
import { toFeatureToggleReducer } from '@members/feature-toggles/state/toggleState';
import { Feature, variables } from '@members/feature-toggles/toggles';
import {
  guestPassSkuReducer,
  GUEST_PASS_SKU_NAMESPACE,
} from '@members/guest-pass/redux/guestPassSku';
import { earlyExitReducer } from '@members/library/Classes/redux';
import { meReducer } from '@members/me/redux';
import memberSearchReducer from '@members/member-search/redux';
import { modalReducer } from '@members/modal/redux';
import {
  NOTIFICATION_NAMESPACE,
  notificationReducer,
} from '@members/notifications/redux';
import filtersReducer from '@members/pg-library/Filters/redux';
import { cardsReducer } from '@members/pg-preferences/redux/cards';
import {
  SUB_TRANSACTION_NAMESPACE,
  transactionsReducer,
} from '@members/pg-preferences/redux/transactions';
import { programsReducer, PROGRAMS_NAMESPACE } from '@members/pg-programs/redux';
import { invitesReducer, INVITES_NAMESPACE } from '@members/pg-schedule/redux';
import videoReducer from '@members/pg-video/redux';
import { referralsReducer } from '@members/referrals';
import { viewedMemberReducer } from '@members/viewed-member/redux';

export const toStoreReducers = (): Record<string, Reducer> => ({
  [CLASS_DETAIL_NAMESPACE]: classDetailReducer,
  [PROGRAMS_NAMESPACE]: programsReducer,
  [FEATURE_TOGGLE_NAMESPACE]: isProdEnv()
    ? toFeatureToggleReducer(Object.values(Feature), variables)
    : toFeatureToggleReducer(
        Object.values(Feature),
        variables,
        mergeEnabledFeaturesWithEnv,
        mergeVariablesWithEnv,
      ),
  [MEMBERS_NAMESPACE]: memberReducer,
  [NOTIFICATION_NAMESPACE]: notificationReducer,
  [SCHEDULE_NAMESPACE]: scheduleReducer,
  [SETTINGS_NAMESPACE]: settingsReducer,
  [SUB_TRANSACTION_NAMESPACE]: transactionsReducer,
  [GUEST_PASS_SKU_NAMESPACE]: guestPassSkuReducer,
  [VIDEO_REDUCER_NAMESPACE]: videoReducer,
  [BUFFER_METRICS_NAMESPACE]: bufferMetricsReducer,
  cards: cardsReducer,
  challenges,
  chat: driftReducer,
  [CLASSES_NAMESPACE]: classesReducer,
  extLinkEnv: extLinkEnvReducer,
  facebookModal: facebookModalReducer,
  fetched: fetchReducer,
  filters: filtersReducer,
  form: formReducer,
  friendCounts: friendCountsReducer,
  friends: friendsReducer,
  [INVITES_NAMESPACE]: invitesReducer,
  library: libraryReducer,
  me: meReducer,
  memberSearch: memberSearchReducer,
  modal: modalReducer,
  [OVERVIEW_NAMESPACE]: overviewReducer,
  referrals: referralsReducer,
  subscriptionPlans: plansReducer,
  subscriptions: subscriptionReducer,
  [USER_NAMESPACE]: userReducer,
  viewedMember: viewedMemberReducer,
  workoutHistory: workoutHistoryReducer,
  workouts: workoutsReducer,
  earlyExit: earlyExitReducer,
});
